// Videos Tab - Vocabulary Video Generator
import { authHeaders } from "../config";
// Generate educational Arabic-English vocabulary videos

import { useState, useCallback, useEffect } from "react";
import styles from "./VideosTab.module.css";

// Modal video generator API URL
const VIDEO_API_URL = "https://digitixllc--vocabulary-video-generator-web-app.modal.run";

interface VocabularyItem {
  arabic: string;
  english: string;
  emoji?: string;
  emoji_category?: string;
}

interface VideoContent {
  title: string;
  subtitle?: string;
  items: VocabularyItem[];
}

interface SocialMedia {
  caption?: string;
  caption_ar?: string;
  caption_en?: string;
  hashtags?: string[];
}

interface VideoJob {
  id: string;
  status: "pending" | "generating_content" | "rendering" | "completed" | "failed";
  progress: number;
  error?: string;
  video_url?: string;
  manifest_url?: string;
  preview_url?: string;
  social_media?: SocialMedia;
  tts_model?: string;
}

// TTS model (Edge TTS only - fast and reliable)
const TTS_MODEL = "edge-tts";

// Default template options
const DEFAULT_TEMPLATES = [
  { id: "lavender", name: "Lavender", description: "Purple/pink gradient, clean modern look", color: "#e8e4f8" },
  { id: "citrus-list", name: "Citrus List", description: "Dark theme with lime accents, numbered list", color: "#151515" },
  { id: "plum-pills", name: "Plum Pills", description: "Pink/plum rounded pills, soft feel", color: "#fdeef0" },
  { id: "mint-outline", name: "Mint Outline", description: "Green/mint with outlined borders", color: "#eef7f2" },
  { id: "butter-navy", name: "Butter Navy", description: "Yellow background with navy pills", color: "#ffe9a8" },
  { id: "cocoa-gold", name: "Cocoa Gold", description: "Brown/cocoa with gold accents", color: "#f6efe6" },
];

// Load custom template names from localStorage
const getCustomTemplateNames = (): Record<string, string> => {
  const saved = localStorage.getItem("customTemplateNames");
  return saved ? JSON.parse(saved) : {};
};

// Save custom template names to localStorage
const saveCustomTemplateNames = (names: Record<string, string>) => {
  localStorage.setItem("customTemplateNames", JSON.stringify(names));
};

// Language options with TTS voice codes
const LANGUAGES = [
  { id: "ar", name: "Arabic", flag: "🇸🇦", edgeVoice: "ar-SA-HamedNeural", qwenSpeaker: "Amira" },
  { id: "zh", name: "Chinese", flag: "🇨🇳", edgeVoice: "zh-CN-YunxiNeural", qwenSpeaker: "Yunxi" },
  { id: "ja", name: "Japanese", flag: "🇯🇵", edgeVoice: "ja-JP-KeitaNeural", qwenSpeaker: "Keita" },
  { id: "ko", name: "Korean", flag: "🇰🇷", edgeVoice: "ko-KR-InJoonNeural", qwenSpeaker: "InJoon" },
  { id: "es", name: "Spanish", flag: "🇪🇸", edgeVoice: "es-ES-AlvaroNeural", qwenSpeaker: "Alvaro" },
  { id: "fr", name: "French", flag: "🇫🇷", edgeVoice: "fr-FR-HenriNeural", qwenSpeaker: "Henri" },
  { id: "de", name: "German", flag: "🇩🇪", edgeVoice: "de-DE-ConradNeural", qwenSpeaker: "Conrad" },
  { id: "it", name: "Italian", flag: "🇮🇹", edgeVoice: "it-IT-DiegoNeural", qwenSpeaker: "Diego" },
  { id: "pt", name: "Portuguese", flag: "🇧🇷", edgeVoice: "pt-BR-AntonioNeural", qwenSpeaker: "Antonio" },
  { id: "ru", name: "Russian", flag: "🇷🇺", edgeVoice: "ru-RU-DmitryNeural", qwenSpeaker: "Dmitry" },
  { id: "hi", name: "Hindi", flag: "🇮🇳", edgeVoice: "hi-IN-MadhurNeural", qwenSpeaker: "Madhur" },
  { id: "tr", name: "Turkish", flag: "🇹🇷", edgeVoice: "tr-TR-AhmetNeural", qwenSpeaker: "Ahmet" },
];

const EMOJI_CATEGORIES = [
  "airport", "travel", "transport", "food", "home", "work",
  "education", "health", "technology", "people", "nature", "general"
];

// Topic suggestions pool (expanded for 20 per batch with no repeats)
const TOPIC_SUGGESTIONS = [
  // Travel & Transportation
  "Airport vocabulary for travelers",
  "Hotel and accommodation terms",
  "Train station vocabulary",
  "Car parts and driving terms",
  "Airplane parts and flight words",
  "Passport and visa vocabulary",
  "Beach and seaside vocabulary",
  "Mountain and hiking terms",
  "Cruise ship vocabulary",
  "Public transportation words",

  // Food & Cooking
  "Kitchen utensils and cooking terms",
  "Food and restaurant vocabulary",
  "Fruits and vegetables vocabulary",
  "Breakfast foods and drinks",
  "Desserts and sweets vocabulary",
  "Spices and herbs vocabulary",
  "Meat and seafood vocabulary",
  "Dairy products vocabulary",
  "Beverages and drinks vocabulary",
  "Baking and pastry terms",

  // Health & Body
  "Medical and healthcare words",
  "Body parts vocabulary",
  "Pharmacy and medicine terms",
  "Hospital and emergency vocabulary",
  "Dental and oral care terms",
  "Mental health vocabulary",
  "Exercise and workout terms",
  "Yoga and meditation vocabulary",
  "First aid vocabulary",
  "Eye care and vision terms",

  // Work & Education
  "Office and workplace vocabulary",
  "School and education vocabulary",
  "University and college terms",
  "Job interview vocabulary",
  "Meeting and presentation terms",
  "Computer and software vocabulary",
  "Business and finance words",
  "Legal and law vocabulary",
  "Science laboratory terms",
  "Mathematics vocabulary",

  // Home & Daily Life
  "House and furniture terms",
  "Bathroom vocabulary",
  "Bedroom vocabulary",
  "Living room furniture",
  "Garden and plants vocabulary",
  "Cleaning supplies vocabulary",
  "Laundry and clothing care",
  "Tools and hardware vocabulary",
  "Home appliances vocabulary",
  "Interior design terms",

  // Nature & Animals
  "Animals and nature words",
  "Farm animals vocabulary",
  "Wild animals vocabulary",
  "Ocean creatures vocabulary",
  "Birds and insects vocabulary",
  "Trees and flowers vocabulary",
  "Weather and seasons vocabulary",
  "Geography and landscape terms",
  "Environmental vocabulary",
  "Space and astronomy terms",

  // People & Relationships
  "Family relationships and terms",
  "Emotions and feelings",
  "Positive personality traits",
  "Negative character traits",
  "Physical appearance vocabulary",
  "Age and life stages vocabulary",
  "Friendship vocabulary",
  "Romance and dating terms",
  "Baby and parenting vocabulary",
  "Wedding vocabulary",

  // Shopping & Money
  "Shopping and money terms",
  "Clothing and fashion terms",
  "Jewelry and accessories vocabulary",
  "Shoe types vocabulary",
  "Supermarket vocabulary",
  "Banking vocabulary",
  "Real estate vocabulary",
  "Insurance vocabulary",
  "Credit card and payment terms",
  "Discount and sale vocabulary",

  // Entertainment & Hobbies
  "Sports and fitness terms",
  "Music and art vocabulary",
  "Movies and cinema vocabulary",
  "Theater and drama terms",
  "Photography vocabulary",
  "Gaming and video games terms",
  "Reading and literature vocabulary",
  "Dance and ballet terms",
  "Crafts and DIY vocabulary",
  "Cooking as hobby vocabulary",

  // Technology & Communication
  "Technology and internet words",
  "Social media vocabulary",
  "Smartphone and apps vocabulary",
  "Email and messaging terms",
  "Cybersecurity vocabulary",
  "Artificial intelligence terms",
  "Programming vocabulary",
  "Photography equipment terms",
  "Audio and sound vocabulary",
  "Video production terms",

  // Language & Grammar
  "Common verbs for beginners",
  "Adjectives to describe things",
  "Greetings and polite expressions",
  "Question words vocabulary",
  "Time expressions vocabulary",
  "Direction and location words",
  "Quantity and measurement terms",
  "Comparison vocabulary",
  "Conjunctions and connectors",
  "Prepositions vocabulary",

  // Colors, Numbers & Basics
  "Colors and shapes vocabulary",
  "Numbers and counting words",
  "Days, months and time expressions",
  "Ordinal numbers vocabulary",
  "Basic opposites vocabulary",
  "Size and dimension vocabulary",
  "Textures and materials vocabulary",
  "Patterns and designs vocabulary",
  "Temperature vocabulary",
  "Speed and distance vocabulary",

  // Safety & Emergency
  "Emergency and safety vocabulary",
  "Fire safety vocabulary",
  "Traffic signs vocabulary",
  "Natural disasters vocabulary",
  "Crime and police vocabulary",
  "Military vocabulary",
  "Survival vocabulary",
  "Warning signs vocabulary",
  "Evacuation vocabulary",
  "Self-defense vocabulary",

  // Professions & Services
  "Professions and jobs vocabulary",
  "Restaurant staff vocabulary",
  "Healthcare professionals vocabulary",
  "Construction workers vocabulary",
  "Creative professions vocabulary",
  "Service industry vocabulary",
  "Education professionals vocabulary",
  "Government jobs vocabulary",
  "Freelance work vocabulary",
  "Entrepreneurship vocabulary",

  // Miscellaneous
  "Travel and tourism words",
  "Holidays and celebrations vocabulary",
  "Religious vocabulary",
  "Politics vocabulary",
  "News and media terms",
  "Postal and shipping vocabulary",
  "Recycling vocabulary",
  "Pet care vocabulary",
  "Cosmetics and beauty vocabulary",
  "Tattoo and piercing vocabulary",
];

export function VideosTab() {
  const [mode, setMode] = useState<"topic" | "manual">("topic");
  const [topic, setTopic] = useState("");
  const [manualContent, setManualContent] = useState<VideoContent>({
    title: "",
    subtitle: "",
    items: [
      { arabic: "", english: "", emoji_category: "general" },
      { arabic: "", english: "", emoji_category: "general" },
      { arabic: "", english: "", emoji_category: "general" },
      { arabic: "", english: "", emoji_category: "general" },
      { arabic: "", english: "", emoji_category: "general" },
    ],
  });
  const [jobs, setJobs] = useState<VideoJob[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollingJobs, setPollingJobs] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [suggestedTopics, setSuggestedTopics] = useState<string[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [usedTopicSuggestions, setUsedTopicSuggestions] = useState<Set<string>>(new Set());
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [isDeletingJobs, setIsDeletingJobs] = useState(false);
  // TTS model is always edge-tts (removed other options for speed)
  const [template, setTemplate] = useState<string>("lavender");
  const [primaryLanguage, setPrimaryLanguage] = useState<string>("ar"); // The language being learned (default Arabic)
  const [customTemplateNames, setCustomTemplateNames] = useState<Record<string, string>>(() => getCustomTemplateNames());
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingTemplateName, setEditingTemplateName] = useState<string>("");
  const [usedJobs, setUsedJobs] = useState<Set<string>>(() => {
    // Load used jobs from localStorage
    const saved = localStorage.getItem("usedVideoJobs");
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  // Load jobs from API
  const loadJobs = useCallback(async () => {
    try {
      const response = await fetch(`${VIDEO_API_URL}/jobs`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error("Failed to load jobs:", err);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Poll for job status updates
  useEffect(() => {
    if (pollingJobs.size === 0) return;

    const interval = setInterval(async () => {
      for (const jobId of pollingJobs) {
        try {
          const response = await fetch(`${VIDEO_API_URL}/jobs/${jobId}`);
          if (response.ok) {
            const job = await response.json();
            setJobs(prev => prev.map(j => j.id === jobId ? job : j));

            // Stop polling if job is done
            if (job.status === "completed" || job.status === "failed") {
              setPollingJobs(prev => {
                const next = new Set(prev);
                next.delete(jobId);
                return next;
              });
            }
          }
        } catch (err) {
          console.error(`Failed to poll job ${jobId}:`, err);
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [pollingJobs]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const body: { topic?: string; content?: VideoContent; tts_model?: string; template?: string; primary_language?: string } = {
        tts_model: TTS_MODEL,
        template: template,
        primary_language: primaryLanguage,
      };

      if (mode === "topic") {
        if (!topic.trim() || topic.length < 10) {
          throw new Error("Topic must be at least 10 characters");
        }
        body.topic = topic;
      } else {
        // Validate manual content
        if (!manualContent.title.trim()) {
          throw new Error("Title is required");
        }
        const validItems = manualContent.items.filter(
          item => item.arabic.trim() && item.english.trim()
        );
        if (validItems.length < 5) {
          throw new Error("At least 5 vocabulary items are required");
        }
        body.content = {
          ...manualContent,
          items: validItems,
        };
      }

      const response = await fetch(`${VIDEO_API_URL}/jobs`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to create job");
      }

      const job = await response.json();
      setJobs(prev => [job, ...prev]);
      setPollingJobs(prev => new Set(prev).add(job.id));

      // Clear form
      if (mode === "topic") {
        setTopic("");
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create video job");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddItem = () => {
    if (manualContent.items.length >= 12) return;
    setManualContent(prev => ({
      ...prev,
      items: [...prev.items, { arabic: "", english: "", emoji_category: "general" }],
    }));
  };

  const handleRemoveItem = (index: number) => {
    if (manualContent.items.length <= 5) return;
    setManualContent(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = (index: number, field: keyof VocabularyItem, value: string) => {
    setManualContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return styles.statusCompleted;
      case "failed": return styles.statusFailed;
      case "rendering": return styles.statusRendering;
      default: return styles.statusPending;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "pending": return "Pending";
      case "generating_content": return "Generating Content";
      case "rendering": return "Rendering Video";
      case "completed": return "Completed";
      case "failed": return "Failed";
      default: return status;
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    if (!confirm("Are you sure you want to delete this job?")) return;

    try {
      const response = await fetch(`${VIDEO_API_URL}/jobs/${jobId}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setJobs(prev => prev.filter(j => j.id !== jobId));
        setPollingJobs(prev => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
        setSelectedJobs(prev => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
      }
    } catch (err) {
      console.error("Failed to delete job:", err);
    }
  };

  // Toggle job selection
  const handleToggleJobSelection = (jobId: string) => {
    setSelectedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  };

  // Select/deselect all jobs
  const handleSelectAllJobs = () => {
    if (selectedJobs.size === jobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(jobs.map(j => j.id)));
    }
  };

  // Delete all selected jobs
  const handleDeleteSelectedJobs = async () => {
    if (selectedJobs.size === 0) return;
    if (!confirm(`Are you sure you want to delete ${selectedJobs.size} job(s)?`)) return;

    setIsDeletingJobs(true);
    try {
      const deletePromises = Array.from(selectedJobs).map(jobId =>
        fetch(`${VIDEO_API_URL}/jobs/${jobId}`, { method: "DELETE" })
      );
      await Promise.all(deletePromises);

      setJobs(prev => prev.filter(j => !selectedJobs.has(j.id)));
      setPollingJobs(prev => {
        const next = new Set(prev);
        selectedJobs.forEach(id => next.delete(id));
        return next;
      });
      setSelectedJobs(new Set());
    } catch (err) {
      console.error("Failed to delete jobs:", err);
    } finally {
      setIsDeletingJobs(false);
    }
  };

  const handleCopySocialMedia = async (job: VideoJob) => {
    if (!job.social_media?.caption) return;

    try {
      await navigator.clipboard.writeText(job.social_media.caption);
      setCopiedId(job.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Sort jobs: in-progress first, then completed, then failed
  const sortedJobs = [...jobs].sort((a, b) => {
    const statusOrder: Record<string, number> = {
      "rendering": 0,
      "generating_content": 1,
      "pending": 2,
      "completed": 3,
      "failed": 4,
    };
    return (statusOrder[a.status] || 5) - (statusOrder[b.status] || 5);
  });

  // Generate random topic suggestions (20 at a time, no repeats)
  const handleGenerateTopicSuggestions = () => {
    // Filter out already-used topics
    const availableTopics = TOPIC_SUGGESTIONS.filter(t => !usedTopicSuggestions.has(t));

    // If we've used most topics, reset the used set
    if (availableTopics.length < 20) {
      setUsedTopicSuggestions(new Set());
      // Use all topics since we're resetting
      const shuffled = [...TOPIC_SUGGESTIONS].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, 20);
      setSuggestedTopics(selected);
      setSelectedTopics(new Set());
      // Mark these as used
      setUsedTopicSuggestions(new Set(selected));
    } else {
      // Shuffle and pick 20 from available topics
      const shuffled = [...availableTopics].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, 20);
      setSuggestedTopics(selected);
      setSelectedTopics(new Set());
      // Add to used set
      setUsedTopicSuggestions(prev => {
        const next = new Set(prev);
        selected.forEach(t => next.add(t));
        return next;
      });
    }
  };

  // Toggle topic selection
  const handleToggleTopic = (topic: string) => {
    setSelectedTopics(prev => {
      const next = new Set(prev);
      if (next.has(topic)) {
        next.delete(topic);
      } else {
        next.add(topic);
      }
      return next;
    });
  };

  // Select all topics
  const handleSelectAllTopics = () => {
    if (selectedTopics.size === suggestedTopics.length) {
      setSelectedTopics(new Set());
    } else {
      setSelectedTopics(new Set(suggestedTopics));
    }
  };

  // Use a single selected topic
  const handleUseTopic = (topic: string) => {
    setTopic(topic);
    setSuggestedTopics([]);
    setSelectedTopics(new Set());
  };

  // Batch generate videos for selected topics
  const handleBatchGenerate = async () => {
    if (selectedTopics.size === 0) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const topicsArray = Array.from(selectedTopics);

      for (const topicItem of topicsArray) {
        const response = await fetch(`${VIDEO_API_URL}/jobs`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ topic: topicItem, tts_model: TTS_MODEL, template: template, primary_language: primaryLanguage }),
        });

        if (response.ok) {
          const job = await response.json();
          setJobs(prev => [job, ...prev]);
          setPollingJobs(prev => new Set(prev).add(job.id));
        }
      }

      // Clear selections
      setSuggestedTopics([]);
      setSelectedTopics(new Set());

    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create batch jobs");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mark job as used when downloading
  const handleDownload = (job: VideoJob) => {
    if (!job.video_url) return;

    // Mark as used
    const newUsedJobs = new Set(usedJobs);
    newUsedJobs.add(job.id);
    setUsedJobs(newUsedJobs);
    localStorage.setItem("usedVideoJobs", JSON.stringify([...newUsedJobs]));

    // Open download link
    window.open(job.video_url, "_blank");
  };

  // Mark job as unused
  const handleMarkUnused = (jobId: string) => {
    const newUsedJobs = new Set(usedJobs);
    newUsedJobs.delete(jobId);
    setUsedJobs(newUsedJobs);
    localStorage.setItem("usedVideoJobs", JSON.stringify([...newUsedJobs]));
  };

  // Get template display name (custom or default)
  const getTemplateName = (templateId: string): string => {
    return customTemplateNames[templateId] || DEFAULT_TEMPLATES.find(t => t.id === templateId)?.name || templateId;
  };

  // Start editing a template name
  const handleStartEditTemplateName = (templateId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingTemplateId(templateId);
    setEditingTemplateName(getTemplateName(templateId));
  };

  // Save template name
  const handleSaveTemplateName = () => {
    if (editingTemplateId && editingTemplateName.trim()) {
      const newNames = { ...customTemplateNames, [editingTemplateId]: editingTemplateName.trim() };
      setCustomTemplateNames(newNames);
      saveCustomTemplateNames(newNames);
    }
    setEditingTemplateId(null);
    setEditingTemplateName("");
  };

  // Reset template name to default
  const handleResetTemplateName = (templateId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newNames = { ...customTemplateNames };
    delete newNames[templateId];
    setCustomTemplateNames(newNames);
    saveCustomTemplateNames(newNames);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Vocabulary Video Generator</h1>
        <p className={styles.subtitle}>
          Create educational Arabic-English vocabulary videos with AI narration
        </p>
      </div>

      {/* Mode Toggle */}
      <div className={styles.modeToggle}>
        <button
          className={`${styles.modeBtn} ${mode === "topic" ? styles.active : ""}`}
          onClick={() => setMode("topic")}
        >
          Generate from Topic
        </button>
        <button
          className={`${styles.modeBtn} ${mode === "manual" ? styles.active : ""}`}
          onClick={() => setMode("manual")}
        >
          Manual Input
        </button>
      </div>

      {/* Input Form */}
      <div className={styles.formCard}>
        {mode === "topic" ? (
          <div className={styles.topicForm}>
            <div className={styles.topicHeader}>
              <label className={styles.label}>Topic</label>
              <button
                type="button"
                onClick={handleGenerateTopicSuggestions}
                className={styles.suggestBtn}
              >
                🎲 Suggest Topics
              </button>
            </div>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g., English vocabulary about airports for Arabic speakers"
              className={styles.input}
            />

            {/* Topic Suggestions */}
            {suggestedTopics.length > 0 && (
              <div className={styles.suggestionsSection}>
                <div className={styles.suggestionsHeader}>
                  <span className={styles.suggestionsTitle}>Suggested Topics:</span>
                  <div className={styles.suggestionsActions}>
                    <button
                      type="button"
                      onClick={handleSelectAllTopics}
                      className={styles.selectAllBtn}
                    >
                      {selectedTopics.size === suggestedTopics.length ? "Deselect All" : "Select All"}
                    </button>
                    {selectedTopics.size > 0 && (
                      <button
                        type="button"
                        onClick={handleBatchGenerate}
                        disabled={isSubmitting}
                        className={styles.batchBtn}
                      >
                        {isSubmitting ? "Creating..." : `Generate ${selectedTopics.size} Videos`}
                      </button>
                    )}
                  </div>
                </div>
                <div className={styles.suggestionsList}>
                  {suggestedTopics.map((suggestedTopic) => (
                    <div
                      key={suggestedTopic}
                      className={`${styles.suggestionItem} ${selectedTopics.has(suggestedTopic) ? styles.selected : ""}`}
                    >
                      <label className={styles.suggestionLabel}>
                        <input
                          type="checkbox"
                          checked={selectedTopics.has(suggestedTopic)}
                          onChange={() => handleToggleTopic(suggestedTopic)}
                          className={styles.suggestionCheckbox}
                        />
                        <span className={styles.suggestionText}>{suggestedTopic}</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => handleUseTopic(suggestedTopic)}
                        className={styles.useTopicBtn}
                        title="Use this topic"
                      >
                        Use
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className={styles.hint}>
              Describe the vocabulary topic you want to create a video for.
              AI will generate 8-10 relevant vocabulary pairs.
            </p>
          </div>
        ) : (
          <div className={styles.manualForm}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Arabic Title</label>
                <input
                  type="text"
                  value={manualContent.title}
                  onChange={(e) => setManualContent(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="تعلم اللغة الإنجليزية"
                  className={styles.input}
                  dir="rtl"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>English Subtitle</label>
                <input
                  type="text"
                  value={manualContent.subtitle || ""}
                  onChange={(e) => setManualContent(prev => ({ ...prev, subtitle: e.target.value }))}
                  placeholder="Airport Vocabulary"
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.itemsHeader}>
              <label className={styles.label}>Vocabulary Items (5-12)</label>
              <button
                onClick={handleAddItem}
                disabled={manualContent.items.length >= 12}
                className={styles.addBtn}
              >
                + Add Item
              </button>
            </div>

            <div className={styles.itemsList}>
              {manualContent.items.map((item, index) => (
                <div key={index} className={styles.itemRow}>
                  <span className={styles.itemNumber}>{index + 1}</span>
                  <input
                    type="text"
                    value={item.arabic}
                    onChange={(e) => handleItemChange(index, "arabic", e.target.value)}
                    placeholder="Arabic word"
                    className={styles.itemInput}
                    dir="rtl"
                  />
                  <input
                    type="text"
                    value={item.english}
                    onChange={(e) => handleItemChange(index, "english", e.target.value)}
                    placeholder="English translation"
                    className={styles.itemInput}
                  />
                  <select
                    value={item.emoji_category || "general"}
                    onChange={(e) => handleItemChange(index, "emoji_category", e.target.value)}
                    className={styles.categorySelect}
                  >
                    {EMOJI_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleRemoveItem(index)}
                    disabled={manualContent.items.length <= 5}
                    className={styles.removeBtn}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Language Settings */}
        <div className={styles.settingsRow}>
          {/* Language Selection */}
          <div className={styles.languageSection}>
            <label className={styles.settingsLabel}>Learning Language:</label>
            <select
              value={primaryLanguage}
              onChange={(e) => setPrimaryLanguage(e.target.value)}
              className={styles.languageSelect}
            >
              {LANGUAGES.map(lang => (
                <option key={lang.id} value={lang.id}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
            <small className={styles.settingsHint}>
              Videos will teach {LANGUAGES.find(l => l.id === primaryLanguage)?.name} vocabulary with English translations
            </small>
          </div>
        </div>

        {/* Template Selection */}
        <div className={styles.templateSection}>
          <label className={styles.settingsLabel}>Video Template:</label>
          <div className={styles.templateGrid}>
            {DEFAULT_TEMPLATES.map(tmpl => (
              <label
                key={tmpl.id}
                className={`${styles.templateOption} ${template === tmpl.id ? styles.templateSelected : ""}`}
                style={{ "--template-color": tmpl.color } as React.CSSProperties}
              >
                <input
                  type="radio"
                  name="template"
                  value={tmpl.id}
                  checked={template === tmpl.id}
                  onChange={(e) => setTemplate(e.target.value)}
                  className={styles.templateRadio}
                />
                <span
                  className={styles.templateSwatch}
                  style={{ background: tmpl.color }}
                />
                <span className={styles.templateInfo}>
                  {editingTemplateId === tmpl.id ? (
                    <span className={styles.templateNameEdit}>
                      <input
                        type="text"
                        value={editingTemplateName}
                        onChange={(e) => setEditingTemplateName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveTemplateName();
                          if (e.key === "Escape") setEditingTemplateId(null);
                        }}
                        onBlur={handleSaveTemplateName}
                        className={styles.templateNameInput}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    </span>
                  ) : (
                    <strong className={styles.templateName}>
                      {getTemplateName(tmpl.id)}
                      <button
                        type="button"
                        onClick={(e) => handleStartEditTemplateName(tmpl.id, e)}
                        className={styles.editNameBtn}
                        title="Rename template"
                      >
                        ✏️
                      </button>
                      {customTemplateNames[tmpl.id] && (
                        <button
                          type="button"
                          onClick={(e) => handleResetTemplateName(tmpl.id, e)}
                          className={styles.resetNameBtn}
                          title="Reset to default name"
                        >
                          ↩️
                        </button>
                      )}
                    </strong>
                  )}
                  <small>{tmpl.description}</small>
                </span>
              </label>
            ))}
          </div>
        </div>

        {error && (
          <div className={styles.error}>{error}</div>
        )}

        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={styles.submitBtn}
        >
          {isSubmitting ? "Creating..." : "Generate Video"}
        </button>
      </div>

      {/* Jobs List */}
      <div className={styles.jobsSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Video Jobs ({jobs.length})</h2>
          {jobs.length > 0 && (
            <div className={styles.jobsHeaderActions}>
              <span className={styles.jobsCount}>
                {jobs.filter(j => j.status === "completed").length} completed
              </span>
              <button
                onClick={handleSelectAllJobs}
                className={styles.selectAllJobsBtn}
              >
                {selectedJobs.size === jobs.length ? "Deselect All" : "Select All"}
              </button>
              {selectedJobs.size > 0 && (
                <button
                  onClick={handleDeleteSelectedJobs}
                  disabled={isDeletingJobs}
                  className={styles.deleteSelectedBtn}
                >
                  {isDeletingJobs ? "Deleting..." : `Delete ${selectedJobs.size} Selected`}
                </button>
              )}
            </div>
          )}
        </div>

        {jobs.length === 0 ? (
          <div className={styles.emptyState}>
            No video jobs yet. Create one above!
          </div>
        ) : (
          <div className={styles.jobsList}>
            {sortedJobs.map((job) => (
              <div key={job.id} className={`${styles.jobCard} ${styles[`jobCard${job.status.charAt(0).toUpperCase() + job.status.slice(1)}`] || ''} ${selectedJobs.has(job.id) ? styles.jobCardSelected : ''}`}>
                <div className={styles.jobHeader}>
                  <div className={styles.jobHeaderLeft}>
                    <input
                      type="checkbox"
                      checked={selectedJobs.has(job.id)}
                      onChange={() => handleToggleJobSelection(job.id)}
                      className={styles.jobCheckbox}
                    />
                    <span className={styles.jobId}>#{job.id}</span>
                    <span className={`${styles.status} ${getStatusColor(job.status)}`}>
                      {getStatusText(job.status)}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteJob(job.id)}
                    className={styles.deleteBtn}
                    title="Delete job"
                  >
                    🗑️
                  </button>
                </div>

                {(job.status === "rendering" || job.status === "generating_content") && (
                  <div className={styles.progressSection}>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${job.progress}%` }}
                      />
                    </div>
                    <span className={styles.progressText}>{job.progress}%</span>
                  </div>
                )}

                {job.error && (
                  <div className={styles.jobError}>{job.error}</div>
                )}

                {job.status === "completed" && (
                  <div className={styles.jobResults}>
                    {job.preview_url && (
                      <a href={job.video_url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={job.preview_url}
                          alt="Video preview"
                          className={`${styles.preview} ${usedJobs.has(job.id) ? styles.previewUsed : ""}`}
                        />
                      </a>
                    )}
                    <div className={styles.jobContent}>
                      {usedJobs.has(job.id) && (
                        <div className={styles.usedBadge}>
                          ✅ Used
                          <button
                            onClick={() => handleMarkUnused(job.id)}
                            className={styles.markUnusedBtn}
                            title="Mark as unused"
                          >
                            ↩️
                          </button>
                        </div>
                      )}
                      <div className={styles.jobActions}>
                        {job.video_url && (
                          <button
                            onClick={() => handleDownload(job)}
                            className={`${styles.downloadBtn} ${usedJobs.has(job.id) ? styles.downloadBtnUsed : ""}`}
                          >
                            {usedJobs.has(job.id) ? "📁 Download Again" : "⬇️ Download Video"}
                          </button>
                        )}
                        {job.social_media?.caption && (
                          <button
                            onClick={() => handleCopySocialMedia(job)}
                            className={styles.copyBtn}
                          >
                            {copiedId === job.id ? "✅ Copied!" : "📋 Copy Caption"}
                          </button>
                        )}
                      </div>

                      {job.social_media?.caption && (
                        <div className={styles.socialMediaSection}>
                          <div className={styles.socialMediaLabel}>Social Media Caption:</div>
                          <div className={styles.socialMediaText}>
                            {job.social_media.caption}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
